<template>
  <div class="custom-field-timestamp" :data-testid="toKebabCase(field.label)">
    <div class="component-item">
      <date-picker
        class="timestamp-date-picker date-picker-input"
        :test-id="`${toKebabCase(field.label)}-date-picker`"
        v-model="customDate"
        placeholder="Select date"
        compact-display
        field-density="compact"
        outlined
        no-margins
        :display-field-label="false"
        :display-field-icon="false"
        :required="field.required"></date-picker>
    </div>
    <div class="component-item">
      <time-entry
        :test-id="`${toKebabCase(field.label)}-time-input`"
        outlined
        :date="dateTime"
        :hide-timezone="hideTimezone"
        :timezone="timezone"
        :military-time-enabled="militaryTimeEnabled"
        v-model="customTime"
        :fontSizeLarge="false"
        :required="field.required"
        :compact="isMobile()"></time-entry>
    </div>
  </div>
</template>

<script>
import customFieldTypeMixin from '@/modules/custom_fields/mixins/customFieldTypeMixin';
import { isMobile } from '@satellite/plugins/util';
import { DateTime } from 'luxon';
import { LuxonDateTimeFormats, toKebabCase } from '@nova/core';

export default {
  name: 'custom-field-timestamp',
  mixins: [customFieldTypeMixin],
  props: {
    timezone: {
      type: String,
      required: true
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hideTimezone: {
      type: Boolean,
      required: false,
      default: false
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      customTime: {
        amPm: '',
        time: ''
      },
      customDate: ''
    };
  },
  computed: {
    dateTime() {
      const dateStr = DateTime.fromISO(this.customDate).toFormat('yyyy-MM-dd');
      // Use 'h:mm' / 'H:mm' (not 'hh:mm' / 'HH:mm') so single-digit hour (e.g. 6:49) parses
      const timeFormat = this.militaryTimeEnabled ? 'H:mm' : 'h:mm a';
      const timeStr = this.militaryTimeEnabled
        ? this.customTime.time
        : [this.customTime.time, this.customTime.amPm].join(' ').trim();
      const newDateTime = DateTime.fromFormat(
        `${dateStr} ${timeStr}`,
        `${LuxonDateTimeFormats.DateDashed} ${timeFormat}`
      ).setZone(this.timezone, { keepLocalTime: true });
      return newDateTime?.isValid ? newDateTime : null;
    }
  },
  methods: {
    toKebabCase,
    isMobile,
    emitValueUpdate(value) {
      if (this.field.required && !value) {
        return null;
      }

      if (this.returnObject) {
        this.$emit('update:model-value', {
          date: this.customDate,
          time: this.customTime.time,
          amPm: this.customTime.amPm
        });
      } else {
        this.$emit('update:model-value', this.dateTime);
      }
    }
  },
  beforeMount() {
    if (this.value) {
      const currentDate = DateTime.fromISO(this.value).setZone(this.timezone);

      if (currentDate?.isValid) {
        this.customDate = currentDate.toFormat(LuxonDateTimeFormats.DateDashed);
        if (this.militaryTimeEnabled) {
          this.customTime.time = currentDate.toFormat(
            LuxonDateTimeFormats.Extended24HrTimeLeadingZeroHour
          );
        } else {
          this.customTime.time = currentDate.toFormat(LuxonDateTimeFormats.Extended12HrTimePadding);
          this.customTime.amPm = currentDate.toFormat(LuxonDateTimeFormats.AMPM).toLowerCase();
        }
      }
    }
  },
  watch: {
    customDate(value) {
      this.emitValueUpdate(value);
    },
    customTime(value) {
      this.emitValueUpdate(value);
    }
  }
};
</script>

<style scoped lang="scss">
.custom-field-timestamp {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
:deep(.time-entry) .v-input:not(.v-autocomplete):not(.v-select):not([class*='mt-']) {
  margin-top: 0;
}

:deep(.time-entry) .input {
  min-width: 6rem;
}

.is-mobile {
  .custom-field-timestamp {
    display: block;
    .component-item {
      margin-bottom: 5px !important;
      .v-input,
      .v-input-control {
        margin: 0 !important;
        padding-top: 5px !important;
        align-items: flex-start !important;
      }
    }
  }
}
</style>
